// A TELA DE RELAÇÕES INSTITUCIONAIS — quem exerce qual papel por quem (feature 009).
//
// Ela responde a pergunta que o vínculo `prefeitura_pai_id` não respondia. O
// vínculo dizia "esta é filial daquela" e não dizia NADA sobre o que isso
// liberava, desde quando, nem por decisão de quem — e não havia tela nenhuma
// onde a resposta pudesse ser lida ou mudada: o parentesco vivia num campo do
// modal de cadastro, ao lado do CNPJ.
//
// TRÊS COISAS QUE ORGANIZAM ESTA TELA:
//
//   1. O RECORTE PADRÃO É A COMPETÊNCIA CORRENTE, e não "tudo". A pergunta que
//      se faz ao abrir é "o que vale hoje?" — e uma lista que já nasce com o
//      histórico inteiro (relações encerradas em 2024 incluídas) responde outra
//      coisa. `competencia=todas` continua a um clique.
//   2. NÃO HÁ EXCLUIR. Encerrar grava a competência final; suspender bloqueia
//      agora sem mexer na vigência. Apagar destruiria a resposta para "quem
//      respondia por esta folha em março?" — e a folha de março continua
//      existindo depois que a relação acaba.
//   3. TODA LINHA AQUI ESTÁ GRAVADA. Durante as fases B e C a tela também
//      mostrava a relação DERIVADA do vínculo antigo, marcada, com um botão
//      "Gravar" no lugar das ações: ela valia de verdade mas não existia para o
//      `PATCH`. A migração da fase C gravou todas, a fase D removeu a derivação
//      (T058), e o que sobra é uma tabela em que toda ação funciona.
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  EstadoErro,
  FilterBar,
  IconButton,
  Pill,
  SelectField,
  TextField,
  type Column,
} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { QUERY_UNIDADES_OPERAVEIS, listarUnidadesOperaveis } from "../../lib/unidades-operaveis";
import { mensagemDeErro } from "../../lib/erro-validacao";
import { AcaoRelacaoModal, NovaRelacaoModal, type AcaoRelacao } from "./relacao-modal";
import {
  PAPEL_OPCOES,
  SITUACAO_RELACAO_OPCOES,
  VARIANTE_SITUACAO,
  competenciaCorrente,
  competenciaValida,
  fmtCompetencia,
  fmtVigencia,
  nomeDaPonta,
  nomeLongoDaPonta,
  rotuloDoPapel,
} from "./relacao-campos";
import { ROTULO_SITUACAO_RELACAO, type Papel, type SituacaoRelacao } from "@atlas/types";
import type { PontaDaRelacao, RelacaoInstitucionalDto } from "@atlas/sdk";

/** O nome de uma ponta, ligado à ficha dela — as duas colunas de organização
 *  são o caminho mais curto entre "vi na lista" e "abri o cadastro". */
function PontaLink({ p }: { p: PontaDaRelacao }) {
  return (
    <Link
      to={`/averbadora/organizacoes/${p.id}?aba=relacoes`}
      style={{ color: "inherit", fontWeight: 600 }}
      title={`${nomeLongoDaPonta(p)}${p.rotuloTipo ? ` — ${p.rotuloTipo}` : ""}`}
    >
      {nomeDaPonta(p)}
    </Link>
  );
}

export function AdminRelacoes() {
  const [params, setParams] = useSearchParams();

  // O RECORTE MORA NA URL, como na tela de órgãos: o suporte cola o link já
  // filtrado ("as relações desta autarquia"), e ele sobrevive ao F5.
  const filtros = {
    responsavel: params.get("responsavel") ?? "",
    atendida: params.get("atendida") ?? "",
    papel: params.get("papel") ?? "",
    // Ausente = a corrente. `todas` desliga o filtro de vigência.
    competencia: params.get("competencia") ?? competenciaCorrente(),
    situacao: params.get("situacao") ?? "",
  };
  const trocar = (patch: Record<string, string>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };
  const limpar = () => trocar({ responsavel: "", atendida: "", papel: "", competencia: "", situacao: "" });
  const temRecorte = !!(
    filtros.responsavel ||
    filtros.atendida ||
    filtros.papel ||
    filtros.situacao ||
    filtros.competencia !== competenciaCorrente()
  );

  // A competência digitada só vai à API quando está completa: um `?competencia=2`
  // no meio da digitação devolveria uma lista vazia e piscaria a tabela a cada tecla.
  const competenciaConsultada =
    filtros.competencia === "todas" || competenciaValida(filtros.competencia)
      ? filtros.competencia
      : competenciaCorrente();

  const consulta = {
    responsavelId: filtros.responsavel ? Number(filtros.responsavel) : undefined,
    atendidaId: filtros.atendida ? Number(filtros.atendida) : undefined,
    papel: (filtros.papel || undefined) as Papel | undefined,
    competencia: competenciaConsultada,
    situacao: (filtros.situacao || undefined) as SituacaoRelacao | undefined,
  };

  const dados = useQuery({
    queryKey: ["admin", "relacoes", "lista", consulta],
    queryFn: () => atlas.admin.relacoes.list(consulta),
  });

  // A lista de organizações serve ao modal e aos dois seletores de ponta. Mesma
  // `queryKey` da tela de órgãos: abrir esta tela depois daquela não custa
  // request nova.
  const organizacoes = useQuery({
    queryKey: QUERY_UNIDADES_OPERAVEIS,
    queryFn: listarUnidadesOperaveis,
  });
  const todas = organizacoes.data?.prefeituras ?? [];

  const [criando, setCriando] = useState(false);
  const [agindo, setAgindo] = useState<{ relacao: RelacaoInstitucionalDto; acao: AcaoRelacao } | null>(null);

  const rows = dados.data?.relacoes ?? [];

  // Os seletores de ponta saem da BASE INTEIRA, não do recorte: uma lista que
  // encolhe conforme se filtra impede justamente o movimento de trocar de
  // organização sem limpar tudo antes.
  const opcoesOrganizacao = [
    { value: "", label: "Todas as organizações" },
    ...todas
      .slice()
      .sort((a, b) => (a.nomeExibicao ?? a.nome).localeCompare(b.nomeExibicao ?? b.nome, "pt-BR"))
      .map((o) => ({ value: String(o.id), label: o.nomeExibicao ?? o.nome })),
  ];

  const columns: Column<RelacaoInstitucionalDto>[] = [
    {
      key: "situacao",
      header: "Situação",
      render: (r) => (
        <div style={{ display: "grid", gap: 4, justifyItems: "start" }}>
          <Pill variant={VARIANTE_SITUACAO[r.situacao]}>{ROTULO_SITUACAO_RELACAO[r.situacao]}</Pill>
        </div>
      ),
    },
    { key: "responsavel", header: "Responsável", render: (r) => <PontaLink p={r.responsavel} /> },
    { key: "atendida", header: "Atendida", render: (r) => <PontaLink p={r.atendida} /> },
    {
      key: "papel",
      header: "Papel",
      render: (r) => <span style={{ fontSize: 12 }}>{r.rotuloPapel || rotuloDoPapel(r.papel)}</span>,
    },
    { key: "vigencia", header: "Vigência", mono: true, render: (r) => fmtVigencia(r) },
    {
      key: "autor",
      header: "Autor",
      render: (r) => (
        <div style={{ display: "grid", gap: 2 }}>
          <span style={{ fontSize: 12 }}>{r.criadaPor}</span>
          {r.suspensaMotivo ? (
            <span style={{ fontSize: 11, color: "var(--gold-500)" }} title={r.suspensaMotivo}>
              suspensa: {r.suspensaMotivo}
            </span>
          ) : (
            <span style={{ fontSize: 11, color: "var(--text-dim)" }}>{r.justificativa}</span>
          )}
        </div>
      ),
    },
    {
      key: "atualizada",
      header: "Atualizada em",
      render: (r) => {
        const quando = r.atualizadaEm ?? r.criadaEm;
        return (
          <div style={{ display: "grid", gap: 2 }}>
            <span style={{ fontSize: 12 }}>{quando ? new Date(quando).toLocaleString("pt-BR") : "—"}</span>
            {r.atualizadaPor ? (
              <span style={{ fontSize: 11, color: "var(--text-dim)" }}>por {r.atualizadaPor}</span>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <span
            style={{
              fontSize: 12,
              letterSpacing: "0.1em",
              fontWeight: 700,
              color: "var(--text-dim)",
              textTransform: "uppercase",
            }}
          >
            Averbadora
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Relações institucionais</h1>
          <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--text-muted)", maxWidth: 720 }}>
            Quem exerce qual papel por quem, e desde quando. A relação <b>dá acesso; não move propriedade</b>{" "}
            — servidores, contratos, convênios e folhas continuam da organização atendida.
          </p>
        </div>
        <Button onClick={() => setCriando(true)}>+ Nova relação</Button>
      </header>

      <FilterBar onReset={limpar}>
        <SelectField
          label="Responsável"
          value={filtros.responsavel}
          onChange={(e) => trocar({ responsavel: e.target.value })}
          options={opcoesOrganizacao}
        />
        <SelectField
          label="Atendida"
          value={filtros.atendida}
          onChange={(e) => trocar({ atendida: e.target.value })}
          options={opcoesOrganizacao}
        />
        <SelectField
          label="Papel"
          value={filtros.papel}
          onChange={(e) => trocar({ papel: e.target.value })}
          options={[{ value: "", label: "Todos os papéis" }, ...PAPEL_OPCOES]}
        />
        <SelectField
          label="Situação"
          value={filtros.situacao}
          onChange={(e) => trocar({ situacao: e.target.value })}
          options={[{ value: "", label: "Todas as situações" }, ...SITUACAO_RELACAO_OPCOES]}
        />
        <TextField
          label="Competência"
          inputMode="numeric"
          value={filtros.competencia === "todas" ? "" : filtros.competencia}
          placeholder="AAAAMM"
          hint={
            filtros.competencia === "todas" ? "mostrando o histórico inteiro" : "vigentes nesta competência"
          }
          onChange={(e) => trocar({ competencia: e.target.value.replace(/\D/g, "").slice(0, 6) })}
        />
        {/* "Todas" é um BOTÃO, e não uma opção do campo de competência: o campo é
            numérico, e digitar a palavra ali não seria descobrível. */}
        <div style={{ display: "flex", alignItems: "flex-end", paddingBottom: 2 }}>
          <Button
            size="sm"
            variant={filtros.competencia === "todas" ? "primary" : "ghost"}
            onClick={() => trocar({ competencia: filtros.competencia === "todas" ? "" : "todas" })}
          >
            {filtros.competencia === "todas" ? "Voltar à corrente" : "Todas as competências"}
          </Button>
        </div>
      </FilterBar>

      {filtros.competencia !== "todas" ? (
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          Mostrando o que vale em <b>{fmtCompetencia(competenciaConsultada)}</b>. Relações encerradas antes
          disso ficam fora do recorte — use <i>Todas as competências</i> para o histórico.
        </div>
      ) : null}

      {dados.isError ? (
        <EstadoErro
          mensagem={mensagemDeErro(dados.error, "não consegui carregar as relações")}
          onTentarDeNovo={() => void dados.refetch()}
        />
      ) : (
        <DataTable
          columns={columns}
          caption="Relações institucionais entre órgãos e entidades"
          rows={rows}
          // A chave por par+papel era exigência da DERIVADA, que não tinha `id`
          // e faria três linhas colidirem numa só. A derivação saiu na fase D
          // (T058) e toda relação tem id — a chave continua tolerando o nulo por
          // um motivo diferente e ainda válido: `id` é `number | null` no tipo, e
          // uma tabela que quebra por causa de um nulo é pior que uma chave
          // composta que ninguém mais usa.
          rowKey={(r) => (r.id != null ? `r${r.id}` : `p${r.responsavelId}-${r.atendidaId}-${r.papel}`)}
          loading={dados.isLoading}
          emptyState={
            temRecorte
              ? "Nenhuma relação para este recorte. Limpe os filtros para ver a lista inteira."
              : "Nenhuma relação institucional. Enquanto não houver, cada organização responde só por si — que é o caso da maioria."
          }
          actions={(r) => (
            <>
                {r.situacao !== "encerrada" ? (
                  <IconButton
                    title="Encerrar"
                    danger
                    onClick={() => setAgindo({ relacao: r, acao: "encerrar" })}
                  >
                    ⏹
                  </IconButton>
                ) : null}
                {r.suspensaEm ? (
                  <IconButton title="Reativar" onClick={() => setAgindo({ relacao: r, acao: "reativar" })}>
                    ▶
                  </IconButton>
                ) : r.situacao !== "encerrada" ? (
                  <IconButton title="Suspender" onClick={() => setAgindo({ relacao: r, acao: "suspender" })}>
                    ⏸
                  </IconButton>
                ) : null}
                <IconButton
                  title="Corrigir vigência"
                  onClick={() => setAgindo({ relacao: r, acao: "corrigir" })}
                >
                  ✎
                </IconButton>
            </>
          )}
        />
      )}

      {criando ? <NovaRelacaoModal organizacoes={todas} onClose={() => setCriando(false)} /> : null}
      {agindo ? (
        <AcaoRelacaoModal relacao={agindo.relacao} acao={agindo.acao} onClose={() => setAgindo(null)} />
      ) : null}
    </div>
  );
}
